import React, { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import Skeleton from "react-loading-skeleton";

import { LabelledRadio, LabelledRadioGroup } from "~/components/ui/labelled-radio";
import { useLoadingDelay } from "~/hooks/use-loading-delay";
import { getClinics, selectClinics, selectClinicsAreLoading } from "~/slices/clinics";
import { addInstructions } from "~/slices/instructions";
import { useAppDispatch, useAppSelector } from "~/store";
import type { TClinic } from "~/types/clinic";
import type { SetDirtyFn } from "~/types/patient-new";
import type { TUser } from "~/types/user";

export function PatientNewDoctorInfo({ user, setDirty }: { user: TUser; setDirty: SetDirtyFn }) {
  const dispatch = useAppDispatch();

  const clinics = useAppSelector(selectClinics);
  const clinicsAreLoading = useLoadingDelay(useAppSelector(selectClinicsAreLoading), {
    delay: 100,
  });
  const clinic_id = useAppSelector((state) => state.instructions.clinic_id);

  useEffect(() => {
    // Store `doctor_id` inside `instructions`
    dispatch(addInstructions({ doctor_id: user.account_id }));
  }, [user, dispatch]);

  useEffect(() => {
    // Load clinics and set `clinic_id` inside `instructions`
    async function loadClinics() {
      const result = await dispatch(getClinics());

      if (getClinics.fulfilled.match(result)) {
        const clinics = result.payload;

        if (clinics.length == 1) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          dispatch(addInstructions({ clinic_id: clinics[0]!.clinic_id }));
        }
      }
    }
    void loadClinics();
  }, [dispatch]);

  function selectClinic(clinic_id: TClinic["clinic_id"]) {
    dispatch(addInstructions({ clinic_id }));

    if (clinics.length > 1) {
      setDirty("clinic_id", clinic_id);
    }
  }

  return (
    <div>
      <h3 className="block" style={{ fontWeight: "900", marginTop: "0" }}>
        <FormattedMessage id="BLOCKHEAD_DOCTOR" />
      </h3>

      <LabelledRadioGroup
        label={<FormattedMessage id="HEADER_CLINIC" />}
        labelProps={{ id: "validation-clinic_id" }}
        required
      >
        {clinicsAreLoading ? (
          <Skeleton className="tw-rounded-md" width={200} height={20} />
        ) : (
          clinics.map((clinic) => (
            <LabelledRadio
              key={clinic.clinic_id}
              label={clinic.title}
              name="clinic"
              value={clinic.clinic_id}
              checked={clinic.clinic_id == clinic_id}
              onChange={(e) => selectClinic(Number(e.target.value))}
            />
          ))
        )}
      </LabelledRadioGroup>
    </div>
  );
}
